import axios from "axios";
import { parseRuntimeRouteConfig, RuntimeRouteEntry } from "./runtimeRouteConfig";
import { overlayRuntimeRoutes } from "./runtimeRouteOverlay";

const ROUTE_CONFIG_URL = "/config/routes.json";

// A per-browser override source for local testing: entries here overlay the deployed base
// by (path, kind), so a developer can try a declaration without shipping it to every player.
const LOCAL_STORAGE_OVERRIDE_KEY = "runtimeRoutesOverride";

// A module-level store rather than state: the route table reading it is a
// module-evaluation constant (App.tsx records why), so the alternative is making
// every consumer reactive for a value written once at bootstrap.
let runtimeRoutes: RuntimeRouteEntry[] = [];

export function setRuntimeRoutes(entries: RuntimeRouteEntry[]): void {
  runtimeRoutes = entries;
}

export function getRuntimeRoutes(): RuntimeRouteEntry[] {
  return runtimeRoutes;
}

function parseDeclaration(raw: string, origin: string): RuntimeRouteEntry[] {
  try {
    return parseRuntimeRouteConfig(JSON.parse(raw));
  } catch (error) {
    // Source-neutral tail: this runs for the env, network and localStorage sources, and a
    // failing override still leaves the base routes in place — so it cannot claim the app has
    // no routes, only that this one source is being ignored.
    console.error(`[runtime-routes] ${origin} is not valid JSON. Ignoring this source.`, error);
    return [];
  }
}

// getItem itself throws when storage is disabled (private mode, blocked cookies), so the
// read is guarded like the network one: a browser that cannot answer contributes nothing
// rather than stopping boot.
function loadOverrideDeclaration(): RuntimeRouteEntry[] {
  let raw: string | null;
  try {
    raw = localStorage.getItem(LOCAL_STORAGE_OVERRIDE_KEY);
  } catch (error) {
    console.error(`[runtime-routes] Could not read localStorage[${LOCAL_STORAGE_OVERRIDE_KEY}]. No route overrides applied.`, error);
    return [];
  }

  if (raw === null) {
    return [];
  }

  return parseDeclaration(raw, `localStorage[${LOCAL_STORAGE_OVERRIDE_KEY}]`);
}

async function loadBaseDeclaration(): Promise<RuntimeRouteEntry[]> {
  const inlineDeclaration = import.meta.env.NANO_PLAYER_FRONTEND_ROUTES;
  if (inlineDeclaration) {
    return parseDeclaration(inlineDeclaration, "NANO_PLAYER_FRONTEND_ROUTES");
  }

  try {
    // A bare axios call rather than HttpService: this runs before the container is
    // wired, and the file is served by the app's own origin rather than by the
    // backend base URL HttpService is built around.
    const response = await axios.get(ROUTE_CONFIG_URL);

    // A server that labels the file text/plain hands back the raw string instead
    // of a parsed body, and both spellings are correct deployments.
    return typeof response.data === "string" ? parseDeclaration(response.data, ROUTE_CONFIG_URL) : parseRuntimeRouteConfig(response.data);
  } catch (error) {
    console.error(`[runtime-routes] Could not read ${ROUTE_CONFIG_URL}. No runtime routes configured.`, error);
    return [];
  }
}

/**
 * Resolves the runtime route declaration. The base arrives from the container env
 * override or, absent that, over the network — this is the one place that knows either:
 * moving it to /rest/settings or an operator dashboard is a change to loadBaseDeclaration
 * and nothing else. A per-browser localStorage override is then layered on top so a
 * developer can test a declaration without changing what every player receives.
 *
 * @returns Every valid declared entry, or an empty list if the declaration is
 *   absent, unreachable or unparseable — never a rejected promise, because a
 *   route declaration must not be able to stop the app from booting.
 */
export async function loadRuntimeRouteConfig(): Promise<RuntimeRouteEntry[]> {
  const base = await loadBaseDeclaration();
  return overlayRuntimeRoutes(base, loadOverrideDeclaration());
}
